import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt, FaClock, FaPhone, FaEnvelope, FaUser,
  FaCommentAlt, FaPaperPlane, FaCheckCircle, FaTimes,
  FaSearch, FaWhatsapp, FaDirections
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const cfg = {
    success: { icon: <FaCheckCircle />, label: "Success", accent: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", border: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />,       label: "Error",   accent: "#8b1a1a", iconBg: "rgba(139,26,26,0.10)", border: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />,      label: "Notice",  accent: "#8a6020", iconBg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.30)", symbol: "ॐ" },
  };
  const c = cfg[type] || cfg.info;

  return (
    <div className="ct-toast-wrap">
      <div className="ct-toast" style={{ "--accent": c.accent, "--border": c.border }}>
        <div className="ct-toast-bar" />
        <span className="ct-tc tl" /><span className="ct-tc tr" />
        <span className="ct-tc bl" /><span className="ct-tc br" />
        <div className="ct-toast-medal" style={{ background: c.iconBg, borderColor: c.border }}>
          <div className="ct-toast-spin" />
          <span style={{ color: c.accent, fontSize: 14, position: "relative", zIndex: 1 }}>{c.icon}</span>
        </div>
        <div className="ct-toast-body">
          <div className="ct-toast-row">
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
            <span className="ct-toast-title" style={{ color: c.accent }}>{c.label}</span>
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
          </div>
          <div className="ct-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accent}55,transparent)` }} />
          <p className="ct-toast-msg">{message}</p>
        </div>
        <button className="ct-toast-x" onClick={onClose} style={{ color: c.accent }}>✕</button>
        <div className="ct-toast-foot" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────── */
function CtStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ══ HERO ══ */
      .ct-hero { display: grid; grid-template-columns: 72px 1fr 72px; align-items: stretch; position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); border-bottom: 1px solid rgba(201,151,58,0.18); }
      .ct-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .ct-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .ct-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: ctRingPulse 4s ease-in-out infinite alternate; }
      .ct-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes ctRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }

      /* Medallion */
      .ct-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,255,255,0.06); }
      .ct-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: ctSlowSpin 10s linear infinite; }
      .ct-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes ctSlowSpin { to { transform: rotate(360deg); } }
      .ct-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .ct-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .ct-hero-om { font-family: "Cinzel Decorative", cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .ct-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .ct-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }

      /* Center */
      .ct-hero-center { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 32px 32px; position: relative; z-index: 2; text-align: center; }
      .ct-hero-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .ct-hero-title { font-family: "Cinzel Decorative", cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .ct-hero-sub { font-family: "EB Garamond", serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 28px; letter-spacing: 0.3px; }
      .ct-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }

      /* Side columns */
      .ct-hero-side-col { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px 0; position: relative; z-index: 2; }
      .ct-side-left { border-right: 1px solid rgba(201,151,58,0.12); }
      .ct-side-right { border-left: 1px solid rgba(201,151,58,0.12); }
      .ct-hlc-om { font-family: "Cinzel Decorative", cursive; font-size: 20px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); line-height: 1; }
      .ct-hlc-line { width: 1px; flex: 1; min-height: 16px; background: linear-gradient(180deg, transparent, rgba(201,151,58,0.3), transparent); }
      .ct-hlc-symbol { font-size: 16px; color: rgba(232,196,106,0.5); line-height: 1; }
      .ct-hlc-tick { width: 2px; border-radius: 1px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); }
      .ct-hlc-top-ornament, .ct-hlc-bot-ornament { display: flex; flex-direction: column; align-items: center; gap: 3px; }

      /* ══ BODY ══ */
      .ct-body { background: var(--parchment); padding: 60px 28px 80px; }
      .ct-inner { max-width: 1100px; margin: 0 auto; }

      /* Section header */
      .ct-sec-head { text-align: center; margin-bottom: 40px; }

      /* Two-column layout: info left, map right */
      .ct-main-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 32px; align-items: start; }
      .ct-sec-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 3px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 10px; }
      .ct-sec-title { font-family: "Cinzel Decorative", cursive; font-size: 22px; font-weight: 700; color: var(--crimson-deep); margin-bottom: 12px; }
      .ct-sec-rule { width: 80px; height: 2px; margin: 0 auto; background: linear-gradient(90deg, transparent, var(--gold-mid), transparent); }

      /* Contact form */
      .ct-form-wrap { background: #fff8f0; border: 1px solid rgba(201,151,58,0.2); padding: 36px 32px; clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px)); }
      .ct-form-title { font-family: "Cinzel", serif; font-size: 10px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 24px; display: flex; align-items: center; gap: 8px; }
      .ct-form-title svg { color: var(--gold-mid); }
      .ct-field { margin-bottom: 18px; }
      .ct-label { font-family: "Cinzel", serif; font-size: 8.5px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-dim); display: block; margin-bottom: 7px; }
      .ct-input-wrap { position: relative; display: flex; align-items: center; }
      .ct-input-icon { position: absolute; left: 13px; color: var(--gold-mid); font-size: 12px; pointer-events: none; z-index: 1; }
      .ct-input { width: 100%; background: rgba(201,151,58,0.04); border: 1px solid rgba(201,151,58,0.25); padding: 11px 14px 11px 36px; font-family: "EB Garamond", serif; font-size: 15px; color: var(--text-dark); outline: none; clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px); transition: border-color 0.2s; }
      .ct-input:focus { border-color: rgba(201,151,58,0.55); background: rgba(201,151,58,0.07); }
      .ct-input::placeholder { color: rgba(74,26,8,0.3); font-style: italic; }
      .ct-textarea { width: 100%; background: rgba(201,151,58,0.04); border: 1px solid rgba(201,151,58,0.25); padding: 11px 14px; font-family: "EB Garamond", serif; font-size: 15px; color: var(--text-dark); outline: none; resize: vertical; min-height: 120px; clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px); transition: border-color 0.2s; }
      .ct-textarea:focus { border-color: rgba(201,151,58,0.55); background: rgba(201,151,58,0.07); }
      .ct-textarea::placeholder { color: rgba(74,26,8,0.3); font-style: italic; }
      .ct-submit-btn { display: inline-flex; align-items: center; gap: 8px; font-family: "Cinzel", serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 45%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(12px 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0% 50%); box-shadow: 0 4px 18px rgba(201,151,58,0.28); transition: all 0.3s ease; margin-top: 8px; }
      .ct-submit-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(201,151,58,0.48); filter: brightness(1.07); }
      .ct-submit-btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }

      /* Info panel */
      .ct-info-wrap { display: flex; flex-direction: column; gap: 20px; }
      .ct-info-card { background: #0e0101; border: 1px solid rgba(232,196,106,0.12); padding: 24px 24px; position: relative; overflow: hidden; clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px)); }
      .ct-info-card::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 20% 20%, rgba(160,35,35,0.15) 0%, transparent 60%); pointer-events: none; }
      .ct-info-card-title { font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); margin-bottom: 16px; display: flex; align-items: center; gap: 8px; position: relative; }
      .ct-info-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; font-family: "EB Garamond", serif; font-size: 15px; color: rgba(232,196,106,0.55); line-height: 1.5; position: relative; }
      .ct-info-row svg { color: var(--gold-mid); font-size: 12px; flex-shrink: 0; margin-top: 4px; }
      .ct-info-row:last-child { margin-bottom: 0; }
      .ct-info-row a { color: rgba(232,196,106,0.7); text-decoration: none; transition: color 0.2s; }
      .ct-info-row a:hover { color: var(--gold-bright); }

      /* Map embed */
      .ct-map-wrap { border: 1px solid rgba(201,151,58,0.2); overflow: hidden; clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px)); height: 100%; }
      .ct-map-header { background: linear-gradient(135deg, #2e0c0c, #1a0404); padding: 14px 24px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid rgba(201,151,58,0.18); }
      .ct-map-header-title { font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-mid); }
      .ct-map-header-pin { color: var(--gold-mid); font-size: 13px; }
      .ct-map-frame { width: 100%; height: 100%; min-height: 460px; border: none; display: block; filter: sepia(0.25) contrast(1.05); }
      .ct-directions-btn { display: inline-flex; align-items: center; gap: 7px; font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, var(--gold-mid), var(--gold-bright)); border: none; padding: 10px 20px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.2s; margin-left: auto; text-decoration: none; }
      .ct-directions-btn:hover { filter: brightness(1.08); }

      /* ══ TOAST ══ */
      .ct-toast-wrap { position: fixed; top: 28px; right: 28px; z-index: 4000; animation: ctToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes ctToastIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
      .ct-toast { width: 340px; background: linear-gradient(145deg, #FDF3E3, #faf0dc, #f5e8cc); border: 1px solid var(--border); border-radius: 2px; box-shadow: 0 12px 32px rgba(26,4,4,0.14); padding: 0 0 14px; overflow: hidden; position: relative; }
      .ct-toast-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .ct-tc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.4); border-style: solid; }
      .ct-tc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .ct-tc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .ct-tc.bl { bottom: 7px; left: 11px; border-width: 0 0 1px 1px; }
      .ct-tc.br { bottom: 7px; right: 11px; border-width: 0 1px 1px 0; }
      .ct-toast-medal { position: absolute; left: 18px; top: 26px; width: 34px; height: 34px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .ct-toast-spin { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.28); animation: ctSpin 8s linear infinite; }
      @keyframes ctSpin { to { transform: rotate(360deg); } }
      .ct-toast-body { padding: 0 40px 0 64px; }
      .ct-toast-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .ct-toast-title { font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .ct-toast-rule { height: 1px; margin-bottom: 6px; }
      .ct-toast-msg { font-family: "EB Garamond", serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.72); line-height: 1.5; }
      .ct-toast-foot { height: 1px; margin: 12px 18px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.22),transparent); }
      .ct-toast-x { position: absolute; top: 20px; right: 13px; background: none; border: none; font-size: 12px; cursor: pointer; opacity: 0.4; transition: opacity 0.2s; }
      .ct-toast-x:hover { opacity: 0.85; }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 768px) {
        .ct-main-grid { grid-template-columns: 1fr; }
        .ct-map-frame { min-height: 300px; height: 300px; }
        .ct-map-wrap { height: auto; }
      }
      @media (max-width: 640px) {
        .ct-hero { grid-template-columns: 32px 1fr 32px; }
        .ct-hlc-om { font-size: 14px; }
        .ct-hero-title { font-size: 20px; }
        .ct-info-card { clip-path: none; border-radius: 14px; }
        .ct-map-wrap { clip-path: none; border-radius: 14px; }
        .ct-body { padding: 36px 16px 60px; }
        .ct-toast-wrap { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .ct-toast { width: 100%; border-radius: 12px; }
      }
    `}</style>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────── */
export default function Contact() {
  const { t } = useTranslation();
  const [logoFace, setLogoFace] = useState("om");
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "info") => setToast({ message, type });

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <CtStyles />
      <Navbar />

      {/* ══ HERO ══ */}
      <div className="ct-hero">
        <div className="ct-hero-bg" />
        <div className="ct-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="ct-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>

        {/* Left side column */}
        <div className="ct-hero-side-col ct-side-left">
          <div className="ct-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ct-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="ct-hlc-om">ॐ</div>
          <div className="ct-hlc-line" />
          <span className="ct-hlc-symbol">◈</span>
          <div className="ct-hlc-line" />
          <div className="ct-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ct-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        {/* Center */}
        <div className="ct-hero-center">
          <div className="ct-hero-medallion">
            <div className="ct-hero-med-ring" />
            <div className="ct-hero-med-ring ct-hero-med-ring-2" />
            <div className={`ct-hero-face ${logoFace === "om" ? "visible" : ""}`}>
              <span className="ct-hero-om">ॐ</span>
            </div>
            <div className={`ct-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
              <img src="./logo.png" alt="Temple" className="ct-hero-logo-img" />
            </div>
            <div className="ct-hero-med-halo" />
          </div>

          <div className="ct-hero-eyebrow">✦ {t("templeName") || "Kodungallur Kavil Temple"} ✦</div>
          <h1 className="ct-hero-title">{t("contactTitle") || "Contact Us"}</h1>
          <p className="ct-hero-sub">{t("contactSub") || "Reach out to the temple — we're here to help"}</p>

          <div className="ct-hero-rule" />
        </div>

        {/* Right side column */}
        <div className="ct-hero-side-col ct-side-right">
          <div className="ct-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ct-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="ct-hlc-om">ॐ</div>
          <div className="ct-hlc-line" />
          <span className="ct-hlc-symbol">◈</span>
          <div className="ct-hlc-line" />
          <div className="ct-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ct-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="ct-body">
        <div className="ct-inner">

          <div className="ct-sec-head">
            <div className="ct-sec-eyebrow">✦ {t("ctFormEyebrow") || "Get In Touch"} ✦</div>
            <h2 className="ct-sec-title">{t("ctFormTitle") || "We'd Love to Hear From You"}</h2>
            <div className="ct-sec-rule" />
          </div>

          <div className="ct-main-grid">
            {/* Left — Info cards */}
            <div className="ct-info-wrap">
              <div className="ct-info-card">
                <div className="ct-info-card-title"><FaMapMarkerAlt /> {t("ctAddressTitle") || "Address"}</div>
                <div className="ct-info-row"><FaMapMarkerAlt /> {t("ctAddress") || "Kodungallor Kavil Sree Bhadra Kali Devi Temple, 8H9C+W79, Parambathurpadi - Budhanoor Rd, Pandanad, Kerala 689506"}</div>
              </div>

              <div className="ct-info-card">
                <div className="ct-info-card-title"><FaPhone /> {t("ctPhoneTitle") || "Phone & Email"}</div>
                <div className="ct-info-row">
                  <FaPhone />
                  <a href="tel:+919447014544">{t("ctPhone") || "+91 94470 14544"}</a>
                </div>
                <div className="ct-info-row">
                  <FaWhatsapp />
                  <a href="https://wa.me/919700000000" target="_blank" rel="noreferrer">{t("ctWhatsapp") || "WhatsApp"}</a>
                </div>
                <div className="ct-info-row">
                  <FaEnvelope />
                  <a href="mailto:temple@kavil.org">{t("ctEmail") || "temple@kavil.org"}</a>
                </div>
              </div>

              <div className="ct-info-card">
                <div className="ct-info-card-title"><FaClock /> {t("ctHoursTitle") || "Temple Hours"}</div>
                <div className="ct-info-row"><FaClock /> {t("ctHoursMorning") || "5:00 AM – 12:30 PM"}</div>
                <div className="ct-info-row"><FaClock /> {t("ctHoursEvening") || "5:00 PM – 10:30 PM"}</div>
              </div>
            </div>

            {/* Right — Map */}
            <div className="ct-map-wrap">
              <div className="ct-map-header">
                <FaMapMarkerAlt className="ct-map-header-pin" />
                <span className="ct-map-header-title">{t("ctMapTitle") || "Find Us · Pandanad, Chengannur"}</span>
                <a
                  className="ct-directions-btn"
                  href="https://maps.app.goo.gl/odfMMVRpSjYmu6FTA"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FaDirections /> {t("ctDirectionsBtn") || "Get Directions"}
                </a>
              </div>
              <iframe
                className="ct-map-frame"
                title="Temple Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1109.6591478185073!2d76.57028086343168!3d9.32038161964659!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b06237f11953447%3A0x179f8c171a966ad2!2sKodungallor%20Kavil%20Sree%20Bhadra%20Kali%20Devi%20Temple!5e0!3m2!1sen!2sin!4v1773427648130!5m2!1sen!2sin"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );
}